import { styled } from '@mui/material/styles';
import type { GhostInputSize } from './GhostInput';

interface RootProps {
  size: GhostInputSize;
  readOnly: boolean;
  grow: boolean;
  bleed: boolean;
}

/** Figma's horizontal padding of each size. */
const INSET_X: Record<GhostInputSize, number> = { medium: 12, small: 8 };

/**
 * Figma `ghost-input` (29834:122500). At rest it is bare text. Hover fills it grey
 * (29868:194089), and typing draws a 1px brand stroke inside with a 2px light-brand
 * ring outside (29834:122501) and no fill. Medium is 8/12 with radius 8, small is 4/8
 * with radius 4.
 *
 * The stroke is a border that is transparent at rest, so the padding is one pixel short
 * of Figma's: the text sits exactly where Figma puts it and does not move when the
 * stroke appears. The outer ring is a box-shadow for the same reason.
 *
 * `bleed` pulls the field out by its horizontal padding, so its text lines up with the
 * content around it (Work performed under its label, Figma 36557:195105) while the
 * hover fill and the typing ring keep their inset. The parent's gutter must hold it.
 */
export const Root = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'size' && prop !== 'readOnly' && prop !== 'grow' && prop !== 'bleed',
})<RootProps>(({ theme, size, readOnly, grow, bleed }) => {
  const brand = theme.palette.custom.brand;
  const insetX = INSET_X[size];
  return {
    boxSizing: 'border-box',
    width: bleed ? `calc(100% + ${2 * insetX}px)` : '100%',
    minWidth: 0,
    margin: bleed ? `0 ${-insetX}px` : 0,
    padding: size === 'small' ? '3px 7px' : '7px 11px',
    border: '1px solid transparent',
    borderRadius: size === 'small' ? 4 : 8,
    backgroundColor: 'transparent',
    cursor: readOnly ? 'default' : 'text',
    // Figma: Raleway 14/20, weight 500, grey/primary.
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.text.primary,
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
    ...(grow && { flex: 1, display: 'flex', flexDirection: 'column' }),
    ...(!readOnly && {
      '&:hover': { backgroundColor: theme.palette.grey[120] },
      // After `:hover`, so a hovered field that is being typed in shows the typing state.
      '&:focus-within': {
        backgroundColor: 'transparent',
        borderColor: brand[100],
        boxShadow: `0 0 0 2px ${brand[25]}`,
      },
    }),

    // The editor inside is ProseMirror (Tiptap) on every surface today. `&&` doubles the
    // field's class, so these rules outrank the editor's own stylesheet whatever the
    // order the two load in.
    '&& .ProseMirror': {
      outline: 'none',
      minHeight: 20,
      overflowWrap: 'anywhere',
    },
    '&& .ProseMirror p': { margin: 0 },
    '&& .ProseMirror p.is-editor-empty:first-of-type::before': {
      content: 'attr(data-placeholder)',
      color: theme.palette.text.placeholder,
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
  };
});
